import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Brain, Briefcase, Dumbbell, X } from 'lucide-react';
import { TreatmentRecord } from '../../../types/treatment';

interface CounselingSectionProps {
  record: Partial<TreatmentRecord>;
  onChange: (updates: Partial<TreatmentRecord>) => void;
  mode?: 'view' | 'edit' | 'create';
}

const workStyleOptions = [
  'デスクワーク中心',
  '立ち仕事中心',
  '座り仕事と立ち仕事半々',
  '力仕事・肉体労働',
  'リモートワーク',
  '不規則な勤務',
  '主婦・主夫',
  '学生',
  'その他',
];

const exerciseFrequencyOptions = [
  '毎日',
  '週4-5回',
  '週2-3回',
  '週1回',
  '月2-3回',
  'ほとんどしない',
  '全くしない',
];

const commonStressFactors = [
  '仕事のプレッシャー',
  '人間関係',
  '家族関係',
  '経済的不安',
  '健康不安',
  '睡眠不足',
  '運動不足',
  '将来への不安',
  '育児ストレス',
  '介護ストレス',
];

export const CounselingSection: React.FC<CounselingSectionProps> = ({
  record,
  onChange,
  mode = 'view',
}) => {
  const [customStressFactor, setCustomStressFactor] = useState('');

  const handleFieldChange = (field: keyof TreatmentRecord, value: unknown) => {
    onChange({ [field]: value });
  };

  const handleLifestyleChange = (field: keyof NonNullable<TreatmentRecord['lifestyle']>, value: unknown) => {
    onChange({
      lifestyle: {
        ...record.lifestyle,
        [field]: value,
      },
    });
  };

  const toggleArrayItem = (item: string) => {
    const currentArray = (record.lifestyle?.stressFactors as string[]) || [];
    const newArray = currentArray.includes(item)
      ? currentArray.filter(i => i !== item)
      : [...currentArray, item];
    handleLifestyleChange('stressFactors', newArray);
  };

  const addCustomStressFactor = () => {
    if (!customStressFactor.trim()) return;
    const currentArray = (record.lifestyle?.stressFactors as string[]) || [];
    if (!currentArray.includes(customStressFactor.trim())) {
      handleLifestyleChange('stressFactors', [...currentArray, customStressFactor.trim()]);
    }
    setCustomStressFactor('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-5 w-5" />
          カウンセリング情報
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* ライフスタイル情報 */}
          <div>
            <h3 className="text-sm font-semibold mb-3">ライフスタイル</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* 仕事スタイル */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-muted-foreground" />
                  仕事スタイル
                </label>
                <Select
                  value={record.lifestyle?.workStyle || ''}
                  onValueChange={(value) => handleLifestyleChange('workStyle', value)}
                  disabled={mode === 'view'}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="選択してください" />
                  </SelectTrigger>
                  <SelectContent>
                    {workStyleOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 運動頻度 */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Dumbbell className="h-4 w-4 text-muted-foreground" />
                  運動頻度
                </label>
                <Select
                  value={record.lifestyle?.exerciseFrequency || ''}
                  onValueChange={(value) => handleLifestyleChange('exerciseFrequency', value)}
                  disabled={mode === 'view'}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="選択してください" />
                  </SelectTrigger>
                  <SelectContent>
                    {exerciseFrequencyOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* ストレス要因 */}
              <div className="space-y-2">
                <label className="text-sm font-medium">ストレス要因</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {commonStressFactors.map((factor) => (
                    <Badge
                      key={factor}
                      variant={(record.lifestyle?.stressFactors || []).includes(factor) ? "destructive" : "outline"}
                      className="cursor-pointer"
                      onClick={() => mode !== 'view' && toggleArrayItem(factor)}
                    >
                      {factor}
                    </Badge>
                  ))}
                </div>
                {mode !== 'view' && (
                  <div className="flex gap-2">
                    <Input
                      value={customStressFactor}
                      onChange={(e) => setCustomStressFactor(e.target.value)}
                      placeholder="カスタム入力"
                      className="text-sm"
                      onKeyPress={(e) => e.key === 'Enter' && addCustomStressFactor()}
                    />
                    <Button size="sm" onClick={addCustomStressFactor}>
                      追加
                    </Button>
                  </div>
                )}
                {(record.lifestyle?.stressFactors || []).some((f: string) => !commonStressFactors.includes(f)) && (
                  <div className="flex flex-wrap gap-2">
                    {(record.lifestyle?.stressFactors || []).filter((f: string) => !commonStressFactors.includes(f)).map((factor: string) => (
                      <Badge
                        key={factor}
                        variant="destructive"
                        className="cursor-pointer"
                      >
                        {factor}
                        {mode !== 'view' && (
                          <X
                            className="ml-1 h-3 w-3"
                            onClick={() => toggleArrayItem(factor)}
                          />
                        )}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* お客様の要望 */}
          <div className="space-y-2">
            <label className="text-sm font-medium">お客様の要望</label>
            <Textarea
              value={record.requests || ''}
              onChange={(e) => handleFieldChange('requests', e.target.value)}
              disabled={mode === 'view'}
              rows={3}
              placeholder="施術に対する要望、重点的にケアしてほしい部位、避けてほしいことなど"
            />
          </div>

          {/* 相談内容 */}
          <div className="space-y-2">
            <label className="text-sm font-medium">相談内容</label>
            <Textarea
              value={record.concerns || ''}
              onChange={(e) => handleFieldChange('concerns', e.target.value)}
              disabled={mode === 'view'}
              rows={3}
              placeholder="お客様からの相談事項、健康に関する悩み、生活習慣の改善希望など"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};